<template>
  <div class="grid gap-4 grid-cols-1 md:grid-cols-3 lg:grid-cols-4 pt-40 pb-32">
    <div
      class="col-span-1 w-full h-fit border-2 border-(--main-color-500) rounded-lg p-4 ms-2"
    >
      <div
        class="grid grid-cols-7 justify-items-center md:justify-items-start items-center"
      >
        <div
          class="text-(--main-color-500) font-bold col-span-7 md:col-span-full mb-4 md:mb-0"
        >
          Filtre
        </div>
        <UInput
          v-model="searchTerm"
          color="neutral"
          highlight
          placeholder="Search..."
          size="lg"
          class="w-full mt-0 md:mt-4 col-span-3 md:col-span-full pr-2 md:pr-0"
        />
        <div
          class="grid gap-2 md:gap-4 grid-cols-2 col-span-4 md:col-span-full w-full"
        >
          <UInput
            v-model="minPrice"
            color="neutral"
            highlight
            placeholder="Prix min"
            size="lg"
            class="w-full mt-0 md:mt-4"
            type="number"
          />
          <UInput
            v-model="maxPrice"
            color="neutral"
            highlight
            placeholder="Prix max"
            size="lg"
            class="w-full mt-0 md:mt-4"
            type="number"
          />
        </div>
        <UInputMenu
          v-model="selectedCategories"
          multiple
          :items="categories"
          color="neutral"
          highlight
          placeholder="Catégorie"
          class="w-full mt-4 col-span-7 md:col-span-full"
        />
        <div
          class="grid gap-2 md:gap-4 grid-cols-2 col-span-4 md:col-span-full w-full mt-4"
        >
          <UCheckbox v-model="isHalal" label="Halal" color="main" size="lg" />
          <UCheckbox v-model="isVegan" label="Vegan" color="main" size="lg" />
        </div>
      </div>
    </div>
    <div
      class="col-span-2 lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 overflow-hidden justify-items-center"
    >
      <MealFlipCard v-for="repa in filteredRepas" :key="repa.id" :food="repa" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Category, Ingredient } from "~/models/repas";

const { $api } = useNuxtApp();
const categories = ref<Category[]>([]);
const { errorToast } = useToastMessages();
const apiUrl = useRuntimeConfig().public.apiUrl;

// Filter states
const searchTerm = ref("");
const minPrice = ref("");
const maxPrice = ref("");
const selectedCategories = ref<string[]>([]);
const isHalal = ref(false);
const isVegan = ref(false);

const { data: repas, refresh: refreshRepas } = await useAsyncData("repas-page", async () => {
  try {
    const response = await $api.get("/repas");
    if (response && Array.isArray(response)) {
      const tmp = [];
      for (let i = 0; i < response.length; i++) {
        const item = response[i];
        tmp.push({
          id: item.id,
          price: item.price,
          name: item.name,
          image: getImageUrl(item.image, apiUrl),
          is_halal: item.is_halal,
          is_vegan: item.is_vegan,
          category: item.category?.name || "",
          category_id: item.category?.id,
          ingredients: item.content
            ? item.content
                .map(
                  (c: Ingredient) =>
                    `${c.quantity} ${c.ingredient?.name || ""}`
                )
                .join(", ")
            : [],
          content: item.content || [],
          group: "Repas",
        });
      }
      return tmp;
    }
    return [];
  } catch (e) {
    console.error("[repas] SSR/fetch error:", e);
    if (import.meta.client) {
      errorToast(
        "Erreur repas",
        "Une erreur est survenu lors du chargement du repas"
      );
    }
    return [];
  }
});

const populateCategories = () => {
  categories.value = [];
  repas.value?.forEach((repa) => {
    if (!categories.value.includes(repa.category)) {
      categories.value.push(repa.category);
    }
  });
};

populateCategories();

onMounted(async () => {
  if (!repas.value || repas.value.length === 0) {
    await refreshRepas();
    populateCategories();
  }
});

// Computed property for filtered repas
const filteredRepas = computed(() => {
  if (!repas.value) return [];

  return repas.value.filter((repa) => {
    // Search filter
    if (
      searchTerm.value &&
      !repa.name.toLowerCase().includes(searchTerm.value.toLowerCase())
    ) {
      return false;
    }

    // Price filter
    if (minPrice.value && repa.price < parseFloat(minPrice.value)) {
      return false;
    }
    if (maxPrice.value && repa.price > parseFloat(maxPrice.value)) {
      return false;
    }

    // Category filter
    if (
      selectedCategories.value.length > 0 &&
      !selectedCategories.value.includes(repa.category)
    ) {
      return false;
    }

    // Halal filter
    if (isHalal.value && !repa.is_halal) {
      return false;
    }

    // Vegan filter
    if (isVegan.value && !repa.is_vegan) {
      return false;
    }

    return true;
  });
});
</script>
